import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FaEnvelopeOpenText, FaCheck, FaCopy } from 'react-icons/fa';
import './ShareDeficienciesGuide.css';

// Standardize unit numbers so PH12 / PH 12 / ph 12 collapse into one entry.
function normalizeUnitNumber(raw) {
  if (raw === null || raw === undefined) return '';
  const value = Array.isArray(raw) ? raw[0] : raw;
  return String(value).trim().toUpperCase().replace(/\s+/g, '');
}

// Natural sort: 110 < 1201 < PH09 < PH12.
function compareUnitNumbers(a, b) {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

function flashCopied(setCopied) {
  setCopied(true);
  setTimeout(() => setCopied(false), 1800);
}

function copyToClipboard(text, setCopied) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(() => flashCopied(setCopied));
}

// Writes both rich HTML and plain text fallback to the clipboard so pasting
// into Gmail (or any rich-text composer) preserves bold/red/italic/links,
// while plain-text email clients still get the readable text version.
function copyHtmlAndText(html, text, setCopied) {
  if (!html && !text) return;

  const ok = () => flashCopied(setCopied);
  const fallback = () =>
    navigator.clipboard.writeText(text || '').then(ok).catch(() => {});

  if (
    html &&
    typeof window !== 'undefined' &&
    typeof window.ClipboardItem !== 'undefined' &&
    navigator.clipboard?.write
  ) {
    try {
      const item = new window.ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text || ''], { type: 'text/plain' }),
      });
      navigator.clipboard.write([item]).then(ok).catch(fallback);
      return;
    } catch (_e) {
      fallback();
      return;
    }
  }

  fallback();
}

// Inline copy box for plain text (Step 1 subject)
function CopyBox({ value, maxHeight }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="sdg-copybox">
      <pre className="sdg-copybox-text" style={maxHeight ? { maxHeight } : undefined}>
        {value}
      </pre>
      <button
        type="button"
        className={`sdg-copy-btn ${copied ? 'sdg-copy-btn--copied' : ''}`}
        onClick={() => copyToClipboard(value, setCopied)}
      >
        {copied ? (
          <>
            <FaCheck /> Copied
          </>
        ) : (
          <>
            <FaCopy /> Copy
          </>
        )}
      </button>
    </div>
  );
}

// Rich-formatted preview. Renders HTML inside the modal so the manager can
// see exactly what the formatted email looks like. Copying is handled by the
// wide pill button at the bottom of the step.
function RichBodyBox({ html, maxHeight }) {
  const previewRef = useRef(null);

  useEffect(() => {
    if (!previewRef.current) return;
    previewRef.current.querySelectorAll('a').forEach((a) => {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    });
  }, [html]);

  return (
    <div className="sdg-copybox sdg-copybox--nopadright">
      <div
        ref={previewRef}
        className="sdg-copybox-text sdg-richbody"
        style={maxHeight ? { maxHeight } : undefined}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  );
}

// Plain readonly box (no inline button) — used in Step 2
function PlainBox({ value, mono = false, maxHeight, expand = false }) {
  const cls =
    `sdg-plainbox` +
    (mono ? ' sdg-plainbox--mono' : '') +
    (expand ? ' sdg-plainbox--expand' : '');
  return (
    <pre className={cls} style={maxHeight ? { maxHeight } : undefined}>
      {value}
    </pre>
  );
}

// Wide pill button at the bottom of a step
function WidePill({ variant = 'primary', icon, children, onClick, href }) {
  const cls = `sdg-pill sdg-pill--${variant}`;
  if (href) {
    // target=_blank keeps the dashboard in the original tab — the browser
    // hands the mailto handoff to the OS in a separate (often auto-closing) tab.
    return (
      <a
        className={cls}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        {icon} <span>{children}</span>
      </a>
    );
  }
  return (
    <button type="button" className={cls} onClick={onClick}>
      {icon} <span>{children}</span>
    </button>
  );
}

// Wide pill copy button — flips to "Copied!" on success
function WidePillCopy({ value, label, variant = 'amber' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={`sdg-pill sdg-pill--${copied ? 'success' : variant}`}
      onClick={() => copyToClipboard(value, setCopied)}
    >
      {copied ? (
        <>
          <FaCheck /> <span>Copied!</span>
        </>
      ) : (
        <>
          <FaCopy /> <span>{label}</span>
        </>
      )}
    </button>
  );
}

// Wide pill copy button that writes BOTH rich HTML and plain text to the
// clipboard so formatting survives a paste into Gmail / condo-management
// mass-mail tools / Outlook / etc.
function WidePillCopyHtml({ html, text, label, variant = 'primary' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className={`sdg-pill sdg-pill--${copied ? 'success' : variant}`}
      onClick={() => copyHtmlAndText(html, text, setCopied)}
    >
      {copied ? (
        <>
          <FaCheck /> <span>Copied!</span>
        </>
      ) : (
        <>
          <FaCopy /> <span>{label}</span>
        </>
      )}
    </button>
  );
}

function StepCard({ number, title, accent = '#2c487d', children }) {
  return (
    <div className="sdg-step">
      <div className="sdg-step-header">
        <div className="sdg-step-number" style={{ backgroundColor: accent }}>
          {number}
        </div>
        <div className="sdg-step-title" style={{ color: accent }}>
          {title}
        </div>
      </div>
      <div className="sdg-step-body">{children}</div>
    </div>
  );
}

function ShareDeficienciesGuide({
  show,
  onClose,
  unitsData = [],
  buildingName = '',
  buildingId = '',
}) {
  const deficient = useMemo(
    () => (unitsData || []).filter((r) => r && r.inspection === 'Needs Service'),
    [unitsData]
  );

  const unitNumbers = useMemo(() => {
    const seen = new Set();
    deficient.forEach((row) => {
      const u = normalizeUnitNumber(row?.unit);
      if (u) seen.add(u);
    });
    return Array.from(seen).sort(compareUnitNumbers);
  }, [deficient]);

  const subject = `Important Notice: Repair Recommended in Your Unit`;

  // Issue-Number gated page: the resident enters the number from the yellow
  // slip left in their suite, so the link cannot open anyone else's unit.
  const lookupUrl = `https://hudsonhvac.ca/unit-access?id=${buildingId || ''}`;
  const safeBuilding = buildingName || '[Building Address]';

  // Plain-text version — used for the mailto: handoff and as the clipboard
  // fallback for plain-text email clients.
  const body =
    `Dear Resident,\n\n` +
    `During the recent in-suite maintenance at ${safeBuilding}, Hudson HVAC found an issue in your unit and a repair is recommended.\n\n` +
    `Hudson left a yellow slip in your suite with an Issue Number on it. Use that number to see your results, including the findings, photos and recommended repairs.\n\n` +
    `1. Go to ${lookupUrl}\n` +
    `2. Enter your Issue Number\n` +
    `3. Enter your email address\n\n` +
    `Can't find your Issue Number? Call Hudson at +1 (437) 448-0640 or email service@hudsonhvac.ca with your unit number and they will send it to you.\n\n` +
    `Hudson is offering discounted building pricing on these repairs when booked directly through the link above.\n\n` +
    `We recommend booking the repair soon to help protect your suite.\n\n` +
    `If you have any questions, please don't hesitate to contact us.\n\n` +
    `Management`;

  // Rich-HTML version — copied to the clipboard alongside the plain text so
  // pasting into Gmail (or any rich-text composer) preserves the formatting.
  // Uses inline styles + simple HTML tags that survive Gmail's sanitizer.
  const bodyHtml = `<div style="font-family: Arial, Helvetica, sans-serif; color: #1f2937; line-height: 1.55; font-size: 14px;">
<p style="margin: 0 0 12px 0;">Dear Resident,</p>
<p style="margin: 0 0 12px 0;">During the recent in-suite maintenance at <strong>${safeBuilding}</strong>, Hudson HVAC found an issue in your unit and a <strong>repair is recommended</strong>.</p>
<p style="margin: 0 0 12px 0;">Hudson left a <strong>yellow slip</strong> in your suite with an <strong>Issue Number</strong> on it. Use that number to see your results, including the findings, photos and recommended repairs.</p>
<ol style="margin: 0 0 12px 0; padding-left: 20px;">
<li style="margin-bottom: 4px;">Go to <a href="${lookupUrl}" target="_blank" rel="noopener noreferrer" style="color: #1d4ed8; text-decoration: underline;">${lookupUrl}</a></li>
<li style="margin-bottom: 4px;">Enter your Issue Number</li>
<li>Enter your email address</li>
</ol>
<p style="margin: 0 0 12px 0;">Can&#39;t find your Issue Number? Call Hudson at <strong>+1 (437) 448-0640</strong> or email <a href="mailto:service@hudsonhvac.ca" style="color: #1d4ed8; text-decoration: underline;">service@hudsonhvac.ca</a> with your unit number and they will send it to you.</p>
<p style="margin: 0 0 12px 0;"><strong>Hudson is offering discounted building pricing on these repairs when booked directly through the link above.</strong></p>
<p style="margin: 0 0 12px 0;">We recommend booking the repair soon to help protect your suite.</p>
<p style="margin: 0 0 12px 0;">If you have any questions, please don&#39;t hesitate to contact us.</p>
<p style="margin: 0;">Management</p>
</div>`;

  const unitList = unitNumbers.join(', ');

  return (
    <Modal show={show} onHide={onClose} size="xl" centered className="sdg-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FaEnvelopeOpenText className="sdg-title-icon" />
          Notify Residents of Deficiencies
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {deficient.length === 0 ? (
          <div className="sdg-empty">
            <FaCheck className="sdg-empty-icon" />
            <div>
              <strong>No follow-ups needed.</strong>{' '}
              No deficient units in this maintenance cycle.
            </div>
          </div>
        ) : (
          <>
            <p className="sdg-intro">
              Send <strong>one email</strong> with all{' '}
              <strong>
                {unitNumbers.length} affected resident{unitNumbers.length === 1 ? '' : 's'}
              </strong>{' '}
              on the BCC line at <strong>{buildingName || 'your building'}</strong>. Each
              recipient will only see their own copy. Follow these 3 steps:
            </p>

            <div className="sdg-steps-grid">
              {/* Step 1 — Email content + Open prefilled */}
              <StepCard number={1} title="Copy Email Notice Template">
                <div className="sdg-substep-label">Subject</div>
                <CopyBox value={subject} />
                <div className="sdg-substep-label sdg-substep-label--spaced">Body</div>
                <RichBodyBox html={bodyHtml} maxHeight={260} />
                <div className="sdg-pill-wrap sdg-pill-wrap--bottom">
                  <WidePillCopyHtml
                    html={bodyHtml}
                    text={body}
                    label="Copy email notice"
                    variant="primary"
                  />
                </div>
              </StepCard>

              {/* Step 2 — Unit numbers */}
              <StepCard
                number={2}
                title="BCC these units that had issues:"
                accent="#b45309"
              >
                <PlainBox value={unitList} mono expand />
                <div className="sdg-pill-wrap sdg-pill-wrap--bottom">
                  <WidePillCopy
                    value={unitList}
                    label={`Copy ${unitNumbers.length} Unit${unitNumbers.length === 1 ? '' : 's'} List`}
                    variant="amber"
                  />
                </div>
              </StepCard>

              {/* Step 3 — Send */}
              <StepCard number={3} title="Send & protect" accent="#16a34a">
                <p className="sdg-step-blurb sdg-step-blurb--lead">
                  Hitting send protects your building from:
                </p>
                <ul className="sdg-bullets">
                  <li>Costly water damage</li>
                  <li>Potential mould growth</li>
                  <li>Premature equipment failure</li>
                  <li>Avoidable resident complaints</li>
                </ul>
                <p className="sdg-step-blurb sdg-step-blurb--strong">
                  Residents will get a link to view their unit's findings and be able to
                  get a free, no-obligation quote for the repair.
                </p>
                <div className="sdg-pill-wrap sdg-pill-wrap--bottom">
                  <WidePill
                    variant="success"
                    icon={<FaCheck />}
                    onClick={onClose}
                  >
                    Done
                  </WidePill>
                </div>
              </StepCard>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ShareDeficienciesGuide;
